/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import {FC, useState, useEffect} from 'react'
import {BrowserRouter, Navigate, Route, Routes, useLocation} from 'react-router-dom'
import {ToastContainer} from 'react-toastify'
import {App} from '../App'
import {AuthLayout, AuthPage, useCognitoAuth} from '../modules/auth'
import {ConfirmRegistration} from '../modules/auth/components/ConfirmRegistration'
import {ErrorsPage} from '../modules/errors/ErrorsPage'
import {PrivateRoutes} from './PrivateRoutes'
import {WelcomePage} from '../pages/WelcomePage'
import {SignInPage} from '../pages/SignInPage'
import {InvitationPage} from '../pages/InvitationPage'
import {ShareInvitationPage} from '../modules/sharing/ShareInvitationPage'
import {ConnectAuthorizePage} from '../pages'
/**
 * Base URL of the website (can be full URL or path).
 * React Router basename must be a path only; strip origin if PUBLIC_URL is a full URL.
 */
function getBasename(): string {
  const url = process.env.PUBLIC_URL || ''
  if (url.startsWith('http')) {
    try {
      return new URL(url).pathname || '/'
    } catch {
      return '/'
    }
  }
  return url || '/'
}

const INVITATION_RETURN_KEY = 'ob_invitation_return'
// One-shot destination stashed when a signed-out visitor follows a deep link
// (e.g. an MCP open_url → /results?file=…), so login returns them to it instead
// of dumping them on /dashboard. Mirrors INVITATION_RETURN_KEY.
const RETURN_TO_KEY = 'ob_return_to'

/** Read and clear (one-shot) the pending post-login return destination. */
function takeReturnTo(): string | null {
  try {
    const dest = sessionStorage.getItem(RETURN_TO_KEY)
    if (dest) sessionStorage.removeItem(RETURN_TO_KEY)
    return dest
  } catch {
    return null
  }
}

/**
 * Unauthenticated catch-all: remember where the visitor was headed, then send
 * them to sign in. The stash is written during render (not an effect) so it lands
 * before the child <Navigate> changes the route. Auth/system paths are not stashed.
 */
const StashAndSignIn: FC = () => {
  const location = useLocation()
  const dest = location.pathname + location.search
  try {
    if (dest && dest !== '/' && !/^\/(signin|auth|welcome|logout|error)(\/|$|\?)/.test(dest)) {
      sessionStorage.setItem(RETURN_TO_KEY, dest)
    }
  } catch {
    /* sessionStorage unavailable — just fall through to a plain sign-in */
  }
  return <Navigate to='/signin' replace />
}

/**
 * Post-login redirect: go to the stashed deep-link destination if there is one,
 * else the given fallback. Reads+clears once per mount (useState initializer).
 */
const PostLoginRedirect: FC<{fallback: string}> = ({fallback}) => {
  const [dest] = useState(() => takeReturnTo() || fallback)
  return <Navigate to={dest} replace />
}

const AppRoutes: FC = () => {
  const {currentUser} = useCognitoAuth()

  // Add a small delay to prevent flash of wrong content during auth initialization
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsReady(true)
    }, 100)

    return () => clearTimeout(timer)
  }, [])

  // After login, check if there's a pending invitation redirect
  const pendingInvitation = currentUser?.sub
    ? sessionStorage.getItem(INVITATION_RETURN_KEY)
    : null

  if (!isReady) {
    return <div>Loading...</div>
  }

  return (
    <BrowserRouter basename={getBasename()}>
      {/*
        Inside the router on purpose. A toast body is an ordinary component tree, and some of them
        (a launched job pointing at the Jobs page) render a <Link>, which reads the router from
        context -- mounted as a sibling of the router, that context is null and the toast throws
        instead of showing.
      */}
      <ToastContainer limit={5} />
      <Routes>
        <Route element={<App />}>
          <Route path='error/*' element={<ErrorsPage />} />
          <Route path='logout' element={<Navigate to='/signin' />} />
          {/* The verification email links here whether or not someone is signed in on this browser. */}
          <Route element={<AuthLayout />}>
            <Route path='confirm-registration' element={<ConfirmRegistration />} />
          </Route>
          {currentUser && currentUser.sub ? (
            <>
              {/* Authenticated routes */}
              <Route path='/*' element={<PrivateRoutes />} />
              {/* After login, prefer a stashed deep-link destination (ob_return_to),
                  then a pending invitation, else the dashboard. */}
              <Route index element={<PostLoginRedirect fallback={pendingInvitation || '/dashboard'} />} />
              <Route path='welcome' element={<PostLoginRedirect fallback={pendingInvitation || '/dashboard'} />} />
              <Route path='signin/*' element={<PostLoginRedirect fallback={pendingInvitation || '/dashboard'} />} />
              {/* Redirect auth pages — if there's a pending invitation, go there instead of dashboard */}
              {pendingInvitation && (
                <Route path='auth/*' element={<Navigate to={pendingInvitation} />} />
              )}
            </>
          ) : (
            <>
              {/* Unauthenticated routes */}
              <Route path='auth/*' element={<AuthPage />} />
              <Route path='welcome' element={<WelcomePage />} />
              <Route path='signin/*' element={<SignInPage />} />
              <Route path='invitation' element={<InvitationPage />} />
              <Route path='share-invitation' element={<ShareInvitationPage />} />
              {/* Consent handles its own sign-in rather than being bounced to /signin: the visitor
                  has just authenticated at the Cognito hosted UI one redirect ago, so sending them
                  back there completes silently instead of asking for a password a second time. */}
              <Route path='connect/authorize' element={<ConnectAuthorizePage />} />
              <Route path='*' element={<StashAndSignIn />} />
            </>
          )}
        </Route>
      </Routes>
    </BrowserRouter>
  )
}

export {AppRoutes}
